import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { withSentryReactRouterV6Routing } from "@sentry/react";

import AtlassianConnectRegisterConfigure from "external/AtlassianConnect";
import GoogleSheetConnect from "external/destination/google_sheet/Connect";
import JiraSyncConnect from "external/destination/jira/Connect";
import JiraSyncNotificationConfig from "external/destination/jira/Notification";
import EnvironmentSourcesMattermostWebhookConnect from "external/destination/mattermost_webhook/Connect";
import MattermostWebhookNotificationConfig from "external/destination/mattermost_webhook/Notification";
import PagerDutyNotificationConfig from "external/destination/pagerduty/Notification";
import SlackNotificationConfig from "external/destination/slack/Notification";
import EnvironmentSourcesSplunkCloudWebhookConnect from "external/destination/splunk_cloud_webhook/Connect";
import SplunkCloudWebhookNotificationConfig from "external/destination/splunk_cloud_webhook/Notification";
import EnvironmentSourcesSplunkWebhookConnect from "external/destination/splunk_webhook/Connect";
import SplunkWebhookNotificationConfig from "external/destination/splunk_webhook/Notification";
import EnvironmentSourcesTeamsWebhookConnect from "external/destination/teams_webhook/Connect";
import TeamsWebhookNotificationConfig from "external/destination/teams_webhook/Notification";
import EnvironmentSourcesTinesWebhookConnect from "external/destination/tines_webhook/Connect";
import TinesWebhookNotificationConfig from "external/destination/tines_webhook/Notification";
import EnvironmentSourcesWebexWebhookConnect from "external/destination/webex_webhook/Connect";
import WebexWebhookNotificationConfig from "external/destination/webex_webhook/Notification";
import EnvironmentSourcesWebhookConnect from "external/destination/webhook/Connect";
import WebhookNotificationConfig from "external/destination/webhook/Notification";
import EnvironmentSourcesZapierWebhookConnect from "external/destination/zapier_webhook/Connect";
import ZapierWebhookNotificationConfig from "external/destination/zapier_webhook/Notification";
import EnvironmentSourcesAWSConnect from "external/source/aws/Connect";
import EnvironmentSourcesAzureConnect from "external/source/azure_sp/Connect";
import EnvironmentSourcesGCPConnect from "external/source/gcp/Connect";
import EnvironmentSourcesGoogleConnect from "external/source/google/Connect";
import EnvironmentSourcesOktaConnect from "external/source/okta/Connect";

import NavLayout from "layouts/NavLayout";
import NoNavLayout from "layouts/NoNavLayout";

import AccountPage from "pages/Account";
import BillingManagePlan from "pages/BillingManagePlan";
import CheckEmailPage from "pages/CheckEmail";
import ComingSoonPage from "pages/ComingSoon";
import Dashboard from "pages/Dashboard";
import Environment from "pages/Environment";
import EnvironmentDestinationsOAuth from "pages/Environment/Destinations/OAuth";
import EnvironmentDestinationsPagerdutyConnect from "pages/Environment/Destinations/Pagerduty";
import EnvironmentSourcesOAuth from "pages/Environment/Sources/OAuth";
import EnvironmentDestinationsSearch from "pages/EnvironmentDestinationsSearch";
import EnvironmentSourcesSearch from "pages/EnvironmentSourcesSearch";
import ErrorPage from "pages/Error";
import IAMFindings from "pages/IAMFindings";
import Login from "pages/Login";
import OrganizationFindings from "pages/OrganizationFindings";
import OrganizationMembers from "pages/OrganizationMembers";
import OrganizationSettings from "pages/OrganizationSettings";
import DcvRedirectWrapper from "pages/OrganizationSettings/General/DCV/RedirectWrapper";
import Register from "pages/Register";
import RiskDashboard from "pages/RiskDashboard";
import FindingsRuleManagement from "pages/RuleManagement";
import StaleFindings from "pages/StaleFindings";

import { LoadRequiredData } from "./LoadRequiredData";

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

// -----------------------------------------------------------------------------

const AppRouter = () => {
  return (
    <BrowserRouter>
      <LoadRequiredData>
        <SentryRoutes>
          <Route element={<NoNavLayout requireNoAuth={true} />}>
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/check-email" element={<CheckEmailPage />} />
          </Route>

          <Route element={<NavLayout />}>
            <Route path="/analytics" element={<Dashboard />} />
            <Route path="/analytics/risk" element={<RiskDashboard />} />
            <Route path="/analytics/iam" element={<IAMFindings />} />
            <Route path="/analytics/stale" element={<StaleFindings />} />

            <Route path="/organization">
              <Route
                path="settings/:module"
                element={<OrganizationSettings />}
              />
              <Route
                path="settings/billing/:module"
                element={<BillingManagePlan />}
              />
              <Route path="members/:module" element={<OrganizationMembers />} />
              <Route path="members" element={<OrganizationMembers />} />

              <Route
                path=""
                element={<Navigate to="settings/general" replace />}
              />
              <Route
                path="settings"
                element={<Navigate to="general" replace />}
              />
              <Route
                path="billing"
                element={
                  <Navigate to="/organization/settings/billing" replace />
                }
              />
              <Route path="dcv/verify/:code" element={<DcvRedirectWrapper />} />
            </Route>

            <Route path="/environment">
              <Route path="" element={<Navigate to="sources" replace />} />

              <Route path="destinations">
                <Route path="" element={<Environment tab="destinations" />} />
                <Route path="all" element={<EnvironmentDestinationsSearch />} />
                <Route
                  path="oauth"
                  element={<EnvironmentDestinationsOAuth />}
                />

                <Route
                  path="slack/:uuid/notifications"
                  element={<SlackNotificationConfig />}
                />
                <Route
                  path="mattermost_webhook/:uuid/notifications"
                  element={<MattermostWebhookNotificationConfig />}
                />
                <Route
                  path="pagerduty/:uuid/notifications"
                  element={<PagerDutyNotificationConfig />}
                />
                <Route
                  path="webhook/:uuid/notifications"
                  element={<WebhookNotificationConfig />}
                />
                <Route
                  path="splunk_webhook/:uuid/notifications"
                  element={<SplunkWebhookNotificationConfig />}
                />
                <Route
                  path="splunk_cloud_webhook/:uuid/notifications"
                  element={<SplunkCloudWebhookNotificationConfig />}
                />
                <Route
                  path="teams_webhook/:uuid/notifications"
                  element={<TeamsWebhookNotificationConfig />}
                />
                <Route
                  path="tines_webhook/:uuid/notifications"
                  element={<TinesWebhookNotificationConfig />}
                />
                <Route
                  path="webex_webhook/:uuid/notifications"
                  element={<WebexWebhookNotificationConfig />}
                />
                <Route
                  path="zapier_webhook/:uuid/notifications"
                  element={<ZapierWebhookNotificationConfig />}
                />
                <Route
                  path="google_sheet/connect"
                  element={<GoogleSheetConnect />}
                />
                <Route
                  path="google_sheet/:uuid/notifications"
                  element={<JiraSyncNotificationConfig />}
                />
                <Route path="jira/connect" element={<JiraSyncConnect />} />
                <Route
                  path="jira/:uuid/notifications"
                  element={<JiraSyncNotificationConfig />}
                />
                <Route
                  path="webhook/connect"
                  element={<EnvironmentSourcesWebhookConnect />}
                />
                <Route
                  path="splunk_webhook/connect"
                  element={<EnvironmentSourcesSplunkWebhookConnect />}
                />
                <Route
                  path="splunk_cloud_webhook/connect"
                  element={<EnvironmentSourcesSplunkCloudWebhookConnect />}
                />
                <Route
                  path="teams_webhook/connect"
                  element={<EnvironmentSourcesTeamsWebhookConnect />}
                />
                <Route
                  path="tines_webhook/connect"
                  element={<EnvironmentSourcesTinesWebhookConnect />}
                />
                <Route
                  path="mattermost_webhook/connect"
                  element={<EnvironmentSourcesMattermostWebhookConnect />}
                />
                <Route
                  path="webex_webhook/connect"
                  element={<EnvironmentSourcesWebexWebhookConnect />}
                />
                <Route
                  path="zapier_webhook/connect"
                  element={<EnvironmentSourcesZapierWebhookConnect />}
                />
                <Route
                  path="slack/redirect"
                  element={
                    <Navigate
                      to={toOauthConnect("destination", "slack")}
                      replace
                    />
                  }
                />
                <Route
                  path="pagerduty/redirect"
                  element={<Navigate to={toPagerDutyConnect()} replace />}
                />
                <Route
                  path="pagerduty/connect"
                  element={<EnvironmentDestinationsPagerdutyConnect />}
                />
              </Route>

              <Route path="sources">
                <Route path="" element={<Environment tab="sources" />} />
                <Route path="all" element={<EnvironmentSourcesSearch />} />
                <Route path="oauth" element={<EnvironmentSourcesOAuth />} />

                <Route
                  path="okta/connect"
                  element={<EnvironmentSourcesOktaConnect />}
                />
                <Route
                  path="google/connect"
                  element={<EnvironmentSourcesGoogleConnect />}
                />
                <Route
                  path="gcp/connect"
                  element={<EnvironmentSourcesGCPConnect />}
                />
                <Route
                  path="aws/connect"
                  element={<EnvironmentSourcesAWSConnect />}
                />
                <Route
                  path="azure/connect"
                  element={<EnvironmentSourcesAzureConnect />}
                />

                <Route
                  path="azure/oauth2"
                  element={
                    <Navigate to={toOauthConnect("source", "azure")} replace />
                  }
                />
                <Route
                  path="box/oauth2"
                  element={
                    <Navigate to={toOauthConnect("source", "box")} replace />
                  }
                />
                <Route
                  path="github/oauth2"
                  element={
                    <Navigate to={toOauthConnect("source", "github")} replace />
                  }
                />
                <Route
                  path="google/oauth2"
                  element={
                    <Navigate to={toOauthConnect("source", "google")} replace />
                  }
                />
                <Route
                  path="m365/oauth2"
                  element={
                    <Navigate to={toOauthConnect("source", "m365")} replace />
                  }
                />
              </Route>

              <Route
                path="atlassian-connect"
                element={<AtlassianConnectRegisterConfigure />}
              />
            </Route>

            <Route path="/account">
              <Route path="" element={<AccountPage />} />
              <Route path="*" element={<Navigate to="" replace />} />
            </Route>
          </Route>

          <Route element={<NavLayout noPadding />}>
            <Route
              path="/organization/findings"
              element={<OrganizationFindings />}
            />
            <Route
              path="/organization/rule_management"
              element={<FindingsRuleManagement />}
            />
            <Route
              path="/organization/audit-log"
              element={<ComingSoonPage />}
            />
          </Route>

          <Route element={<NoNavLayout requireNoAuth={false} />}>
            <Route path="/misc/error/:code" element={<ErrorPage />} />
            <Route path="/misc/:code" element={<ErrorPage />} />
            <Route path="*" element={<ErrorPage />} />
          </Route>
        </SentryRoutes>
      </LoadRequiredData>
    </BrowserRouter>
  );
};

export default AppRouter;

// -----------------------------------------------------------------------------

function toOauthConnect(type: string, service: string) {
  return `/environment/${type}s/oauth?service=${service}&${window.location.search.substring(
    1
  )}`;
}

function toPagerDutyConnect() {
  /* nosemgrep: typescript.react.security.audit.react-router-redirect.react-router-redirect */
  return `/environment/destinations/pagerduty/connect?${window.location.search.substring(
    1
  )}`;
}
